import React from 'react';
import Footer from "./footer";
import Header from "./header";
import ReactGA from "react-ga4";
import screenshot1 from '../assets/1.png';
import screenshot2 from '../assets/2.png';
import screenshot3 from '../assets/3.png';
import screenshot4 from '../assets/4.png';
import screenshot5 from '../assets/5.png';

ReactGA.send(window.location.pathname);

const features = [
    {
        icon: '🏥',
        title: 'Health Scan Insights',
        description: 'Get AI observations on your pet\'s appearance and visible health indicators from a simple photo.',
    },
    {
        icon: '💩',
        title: 'Fecal Analysis',
        description: 'Photograph and track stool consistency over time. Share summaries with your vet when something changes.',
    },
    {
        icon: '👁',
        title: 'Eye & Coat Checks',
        description: 'Monitor eye clarity and coat condition with each scan. Track trends so you can catch changes early.',
    },
    {
        icon: '🐾',
        title: 'Skin & Coat Tracking',
        description: 'Log and compare coat and skin appearance over time across multiple pets.',
    },
    {
        icon: '📸',
        title: 'Breed Identification',
        description: 'Curious about your rescue\'s mix? Get a breed breakdown from a single photo.',
    },
    {
        icon: '🔔',
        title: 'Reminders',
        description: 'Set reminders for vet visits, medications, flea treatments, and more — so nothing falls through the cracks.',
    },
];

const plans = [
    {
        name: 'Free',
        emoji: '🐾',
        scans: '3 scans / month',
        pets: '1 pet',
        highlight: false,
    },
    {
        name: 'Starter',
        emoji: '🌿',
        scans: '15 scans / month',
        pets: 'Up to 3 pets',
        highlight: false,
    },
    {
        name: 'Pro',
        emoji: '⭐',
        scans: '60 scans / month',
        pets: 'Unlimited pets',
        highlight: true,
    },
    {
        name: 'Breeder',
        emoji: '🏆',
        scans: '300 scans / month',
        pets: 'Unlimited pets',
        highlight: false,
    },
];

function Home() {
    return (
        <div className="min-h-screen flex flex-col bg-bg font-sans">
            <Header />

            <div className="flex-1">
                <main className="flex pt-32 flex-col items-center justify-center p-8 gap-2">

                    {/* Hero */}
                    <div className="bg-brand text-white text-sm font-semibold px-3 py-1 rounded-full mb-4">
                        Pet health insights
                    </div>

                    <h1 className="text-4xl font-extrabold tracking-tight sm:text-6xl text-center max-w-3xl leading-tight text-text">
                        Your pet can't tell you when something looks off.
                        <br />
                        <span className="text-brand">Peticle can.</span>
                    </h1>

                    <p className="text-xl text-center max-w-2xl text-muted mt-4 mb-6">
                        Take a photo and get instant observations on your pet's coat, eyes, skin, fecal health,
                        and more — all from your phone.
                    </p>

                    {/* App Store Badges */}
                    <section className="flex flex-col items-center mb-8">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <a
                                target="_blank"
                                rel="noreferrer"
                                className="flex shadow-xl text-decoration-none items-center justify-center gap-2 bg-black text-white rounded-lg px-4 py-2 w-48 h-14"
                                href="https://apps.apple.com/us/app/peticle/id6766522201"      >
                                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 384 512"
                                     className="h-6 w-6" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                                </svg>
                                <div className="flex flex-col">
                                    <span className="text-xs">Download on the</span>
                                    <span className="text-lg font-semibold">App Store</span>
                                </div>
                            </a>
                            <a
                                target="_blank"
                                rel="noreferrer"
                                className="flex shadow-xl text-decoration-none items-center justify-center gap-2 bg-black text-white rounded-lg px-4 py-2 w-48 h-14"
                                href="https://play.google.com/store/apps/details?id=com.unit326.peticle&hl=en"
                            >
                                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512"
                                     className="h-5 w-5" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
                                </svg>
                                <div className="flex flex-col">
                                    <span className="text-xs">Get it on</span>
                                    <span className="text-lg font-semibold">Google Play</span>
                                </div>
                            </a>
                        </div>
                    </section>

                    {/* Screenshot gallery */}
                    <div className="bg-brand-light flex flex-wrap justify-center rounded-3xl p-5 gap-8 mb-12">
                        <img src={screenshot1} alt="Peticle app screenshot 1" width={220} height={480} className="rounded-3xl shadow-lg" />
                        <img src={screenshot5} alt="Peticle app screenshot 2" width={220} height={480} className="rounded-3xl shadow-lg" />
                        <img src={screenshot2} alt="Peticle app screenshot 3" width={220} height={480} className="rounded-3xl shadow-lg" />
                        <img src={screenshot4} alt="Peticle app screenshot 4" width={220} height={480} className="rounded-3xl shadow-lg" />
                        <img src={screenshot3} alt="Peticle app screenshot 5" width={220} height={480} className="rounded-3xl shadow-lg" />
                    </div>

                    {/* Features */}
                    <section className="w-full max-w-5xl mt-4 mb-12">
                        <div className="text-center mb-10">
                            <span className="bg-brand-light text-brand-light-text text-sm px-3 py-1 rounded-full font-semibold">
                                What Peticle does
                            </span>
                            <h2 className="text-3xl font-extrabold tracking-tight text-center text-text mt-3">
                                Stay on top of your pet's wellbeing
                            </h2>
                            <p className="text-muted mt-2 max-w-xl mx-auto">
                                Spot changes early, track health over time, and keep everything in one place — between vet visits and beyond.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {features.map((f) => (
                                <div key={f.title} className="flex flex-col p-6 bg-surface border border-border rounded-2xl hover:border-brand transition-colors">
                                    <div className="text-4xl mb-3">{f.icon}</div>
                                    <h3 className="text-lg font-bold text-text mb-2">{f.title}</h3>
                                    <p className="text-muted text-sm">{f.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Plans teaser */}
                    <section className="w-full max-w-4xl mb-12">
                        <div className="text-center mb-8">
                            <span className="bg-brand-light text-brand-light-text text-sm px-3 py-1 rounded-full font-semibold">
                                Built for pet owners & breeders
                            </span>
                            <h2 className="text-3xl font-extrabold tracking-tight text-text mt-3">
                                A plan for every pet lover
                            </h2>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
                            {plans.map((plan) => (
                                <div
                                    key={plan.name}
                                    className={`flex flex-col items-center p-6 rounded-2xl w-full sm:w-48 border transition-all
                                        ${plan.highlight
                                            ? 'border-brand bg-brand-light'
                                            : 'border-border bg-surface'}`}
                                >
                                    {plan.highlight && (
                                        <span className="bg-brand text-white text-xs font-semibold px-2 py-0.5 rounded-full mb-2">Most Popular</span>
                                    )}
                                    <div className="text-2xl mb-1">{plan.emoji}</div>
                                    <h3 className="text-lg font-bold text-text mb-1">{plan.name}</h3>
                                    <p className="text-brand font-semibold text-sm">{plan.scans}</p>
                                    <p className="text-muted text-sm">{plan.pets}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Disclaimer */}
                    <div className="max-w-2xl w-full bg-[#FFF8E7] border border-[#FCD34D] rounded-2xl p-5 mb-8 text-center">
                        <p className="text-[#78350F] text-sm">
                            <strong>Important:</strong> Peticle provides informational insights only. It is not a veterinary diagnostic tool and does not replace professional veterinary advice, examination, or treatment. Always consult a qualified vet for your pet's health concerns.
                        </p>
                    </div>

                </main>
            </div>

            <Footer />
        </div>
    );
}

export default Home;
